import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { isNil } from 'lodash';
import { DoenKidsGenericApiProvider } from 'src/providers/generic.provider';
import { IAiConfig, IAiUsageQuota, IAiUsageQuotaUsage } from 'typings/doenkids/doenkids';

/** A page of quota windows, as the API returns it. */
export interface IAiUsageQuotaUsageListResponse {
  items: IAiUsageQuotaUsage[];
  total: number;
  limit: number;
  skip: number;
}

/** The quota window length, as the database column allows it. */
export type TAiQuotaPeriod = 'daily' | 'weekly' | 'monthly';

/** The AI configuration as written. `quota_period` is only accepted on creation. */
export interface IAiConfigUpdate {
  token_limit: number;
  /** Only 'monthly' is accepted in v1, though the column allows more. Sent on creation only */
  quota_period?: 'monthly';
  archived?: boolean;
}

/**
 * The per-customer AI configuration, its token budget, and the quota windows
 * spent against it.
 *
 * Every route here is DoenKids-administrator only, so callers must gate their
 * UI on `DoenkidsSessionProvider.isAdmin$` — the API makes the same check and
 * will 403 otherwise.
 */
@Injectable({
  providedIn: 'root',
})
export class OrganizationUnitAiConfigService {
  constructor(private $baseApi: DoenKidsGenericApiProvider) {}

  /**
   * Fetches the active AI configuration, or null when AI is not switched on.
   *
   * The API reads through its archived filter, so an archived config answers
   * 204 (a null body) exactly like a missing one: to a reader, "switched off"
   * and "never configured" are the same state.
   */
  async fetch(organizationUnitId: number): Promise<IAiConfig | null> {
    const config = (await firstValueFrom(
      this.$baseApi.genericGetCallWithoutErrorHandler(`/customer/organization-unit/${organizationUnitId}/ai-config`),
    )) as IAiConfig | null;

    return isNil(config) ? null : config;
  }

  /**
   * Creates or updates the AI configuration. A PUT upsert, since `customer_id`
   * is unique, and it revives an archived row rather than adding a second.
   *
   * The API rejects: a `token_limit` that is not a positive NUMBER, a
   * `quota_period` other than 'monthly', a `quota_period` that differs from the
   * stored one, and a unit that does not resolve to itself as its own customer.
   * Send `quota_period` on creation only.
   *
   * Note it parseInts the budget rather than rejecting a fraction, so 1.5 is
   * stored as 1 without a word. The form validates for integers itself.
   */
  async update(organizationUnitId: number, config: IAiConfigUpdate) {
    return (await firstValueFrom(
      this.$baseApi.genericPutCall(`/customer/organization-unit/${organizationUnitId}/ai-config`, config),
    )) as IAiConfig;
  }

  /**
   * Lists the quota windows with their usage, newest first.
   *
   * Paginated by the API, which defaults to 20. A monthly window means twelve
   * rows a year, so one page of 100 covers eight years of history and the
   * portal has no paging controls to build. `total` is returned alongside so a
   * caller can tell when that stops being true.
   *
   * Reads the same view activity-api's quota gate reads, so this can never
   * disagree with the budget a user actually runs into.
   */
  async fetchUsageQuotas(organizationUnitId: number, limit = 100, skip = 0) {
    let url = `/customer/organization-unit/${organizationUnitId}/ai-usage-quota`;

    url = this.$baseApi.appendQueryParam(url, 'limit', `${limit}`);
    url = this.$baseApi.appendQueryParam(url, 'skip', `${skip}`);

    const response = (await firstValueFrom(
      this.$baseApi.genericGetCallWithoutErrorHandler(url),
    )) as IAiUsageQuotaUsageListResponse | null;

    return {
      items: response?.items ?? [],
      total: response?.total ?? 0,
    };
  }

  /**
   * Changes the token limit of one quota window.
   *
   * Each window keeps its own limit, copied from the config when the window was
   * created, so editing the config only affects FUTURE windows. Raising the
   * budget for the month already running means editing that window here.
   */
  async updateUsageQuota(organizationUnitId: number, aiUsageQuotaId: number, tokenLimit: number) {
    return (await firstValueFrom(
      this.$baseApi.genericPutCall(
        `/customer/organization-unit/${organizationUnitId}/ai-usage-quota/${aiUsageQuotaId}`,
        { token_limit: tokenLimit },
      ),
    )) as IAiUsageQuota;
  }
}
